import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { AuthenticationService } from '../auth/authentication-service';
import { UserService } from '../user/user.service';
import { DataStorage } from '../data/data-storage';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AnalyticsEvent, InternalAnalyticsService } from '../internal-analytics.service';
import { OnboardingStep } from '../../onboarding/onboarding.service';
import { GuidedSetupStepType } from '../../guided-setup/guided-setup.service';
import { requiresRegistrationCard } from '../helpers/signup-variant';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationGuard {
  persistentStorage: DataStorage = DataStorage.persistent();
  constructor(private auth: AuthenticationService, private router: Router, private user: UserService, private analytics: InternalAnalyticsService) {
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    if (this.auth.isAuthenticated()) {
      // A just-carded B/D user's cached data can predate the card submit (the card
      // verifies the email server-side). Trusting the stale cache bounces them to
      // the verification step — and since this guard never refetches while a cache
      // exists, that bounce can loop until an unrelated /user response lands.
      // Refetch instead when the cache says unverified right after a card submit.
      const cacheIsStaleAfterCardSubmit = !!this.user.userData
        && !this.user.userData.emailVerifiedAt
        && !!this.persistentStorage.get('registrationCardSubmitted');
      const request = this.user.userData && !cacheIsStaleAfterCardSubmit ? of(this.user.userData) : this.user.getUser();
      return request.pipe(
        switchMap((user) => {
          const bidParam = route.queryParams['bid'];
          if (bidParam && user.business?.id !== bidParam) {
            return this.user.switchToBusiness(bidParam).pipe(map(() => user), catchError(error => of(user)))
          }
          return of(user);
        }),
        map((user) => {
          this.analytics.identify();

          if (!user?.emailVerifiedAt) {
            return this.router.createUrlTree(['/register', { step: OnboardingStep.EmailVerification }]);
          }

          if (this.user.hasTrialExpired) {
            const currentPath = state.url;
            const isOnSetupRoute = currentPath.includes('/setup');
            const isOnLaunchStep = isOnSetupRoute &&
              (currentPath.includes('launch') || route.params['step'] === GuidedSetupStepType.Launch);

            if (!isOnLaunchStep) {
              return this.router.createUrlTree(['/setup', { step: GuidedSetupStepType.Launch }]);
            }

            return true;
          }

          const isRegisteringWithAnyMethod = this.persistentStorage.get('isRegistering') || this.persistentStorage.get('isRegisteringWithCredentials');
          if (isRegisteringWithAnyMethod) {
            this.analytics.trackEvent(AnalyticsEvent.UserRegisteredAccount);
            this.persistentStorage.set('fireInitialTracking', true);
            this.persistentStorage.remove('lead');
            this.persistentStorage.remove('chosenPath');
            if (this.persistentStorage.get('isRegistering')) {
              this.analytics.trackEvent(AnalyticsEvent.GoogleSSORegister);
              this.persistentStorage.remove('isRegistering');
              this.persistentStorage.remove('isRegisteringWithCredentials');

              // Variant B's card requirement is handled before the shell — AuthCallback
              // routes business-less B users to the public card page — and by the gate
              // below for any business-with-no-card edge, so no redirect is needed here.
              return this.router.createUrlTree(['/setup'], { queryParams: { first_load: 'true' } });
            } else if (this.persistentStorage.get('isRegisteringWithCredentials')) {
              this.persistentStorage.remove('isRegistering');
              this.persistentStorage.remove('isRegisteringWithCredentials');
              this.analytics.trackEvent(AnalyticsEvent.UserFormRegister);
            }
          }

          // Variant B/D must have a card on file before using the app. Catches a
          // verified card-at-creation user who still has no card (e.g. while a just-
          // submitted card finishes attaching) on every entry, including later logins.
          // The marker lets a just-submitted card through during that attach window;
          // it's cleared once a card is confirmed.
          const cardAdded = !!user?.subscription?.isCreditCardAdded;
          if (cardAdded) {
            this.persistentStorage.remove('registrationCardSubmitted');
          } else if (requiresRegistrationCard(user?.signupVariant)
            && !state.url.includes('/complete-signup')
            && !this.persistentStorage.get('registrationCardSubmitted')) {
            return this.router.createUrlTree(['/complete-signup']);
          }

          return true;
        }),
        catchError(error => {
          console.error('Error loading user data:', error);
          this.auth.clearAuthData();
          return of(this.router.createUrlTree(['/login']));
        })
      );
    } else {
      this.auth.clearAuthData();
      return this.router.createUrlTree(['/login']);
    }
  }
}
